import { getFormattedCurrency, getFormattedDecimal, getFormattedPercentage, processDate } from "../";
import { createFormatterWithNullCheck, createEvolutionFormatter } from '../transformers';
import moment from "moment";
import { APP_PERIOD_DROPDOWN_ELEMENTS, DEFAULT_DATE_FORMATS } from "../constants";
const PERIOD_GRANULARITIES = {
  DAILY: APP_PERIOD_DROPDOWN_ELEMENTS.DAILY.value,
  WEEKLY: APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value,
  MONTHLY: APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY.value,
  QUARTERLY: APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY.value,
  YEARLY: APP_PERIOD_DROPDOWN_ELEMENTS.YEARLY.value
};
const UNIT_LABELS = {
  [APP_PERIOD_DROPDOWN_ELEMENTS.DAILY.value]: {
    singular: 'day',
    plural: 'days'
  },
  [APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value]: {
    singular: 'week',
    plural: 'weeks'
  },
  [APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY.value]: {
    singular: 'month',
    plural: 'months'
  },
  [APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY.value]: {
    singular: 'quarter',
    plural: 'quarters'
  },
  [APP_PERIOD_DROPDOWN_ELEMENTS.YEARLY.value]: {
    singular: 'year',
    plural: 'years'
  }
};
const _generatePeriods = (unit, max = 13) => {
  const periods = [];
  const labels = UNIT_LABELS[unit];
  if (!labels) {
    console.warn(`Unknown unit for period generation: ${unit}`);
    return periods;
  }
  for (let i = max; i >= 1; i--) {
    const text = i === 1 ? labels.singular : `${i} ${labels.plural}`;
    const key = `${i}_${unit.toUpperCase()}`;
    periods.push({
      text,
      key,
      value: i,
      unit: labels.plural
    });
  }
  return periods;
};
const PERIOD_OPTIONS = {
  [PERIOD_GRANULARITIES.DAILY]: _generatePeriods(PERIOD_GRANULARITIES.DAILY),
  [PERIOD_GRANULARITIES.WEEKLY]: _generatePeriods(PERIOD_GRANULARITIES.WEEKLY),
  [PERIOD_GRANULARITIES.MONTHLY]: _generatePeriods(PERIOD_GRANULARITIES.MONTHLY),
  [PERIOD_GRANULARITIES.QUARTERLY]: _generatePeriods(PERIOD_GRANULARITIES.QUARTERLY),
  [PERIOD_GRANULARITIES.YEARLY]: _generatePeriods(PERIOD_GRANULARITIES.YEARLY)
};
const CHART_PERIOD_COMPARE_OPTIONS = [{
  text: 'Previous Period',
  value: "prev",
  label: 'prev period'
}, {
  text: 'Year over Year',
  value: "yoy",
  label: 'year on year'
}];
const EVOLUTION_MODES = {
  POSITIVE: "POSITIVE",
  NEGATIVE: "NEGATIVE"
};
const CHART_THEME = {
  colors: {
    light: {
      name: "LIGHT THEME",
      background: "#FFFFFF",
      scaleColor: '#F4F4F4',
      textColor: '#757B78',
      gridColor: '#F4F4F4',
      evolution: {
        red: '#DB333B',
        green: '#22863D'
      },
      accents: [{
        background: "#FFFFFF",
        left: "#E94E1B",
        right: "#312783",
        color: "#4D4D4D"
      }, {
        background: "#FFFFFF",
        left: "#8D85E5",
        right: "#2172E1",
        color: "#4D4D4D"
      }, {
        background: "#FFFFFF",
        left: "#FF8933",
        right: "#E16098",
        color: "#4D4D4D"
      }, {
        background: "#FFFFFF",
        left: "#57C5FF",
        right: "#A666FF",
        color: "#4D4D4D"
      }]
    },
    dark: {
      name: "DARK THEME",
      background: "#4B4B4B",
      scaleColor: '#ADADAD',
      textColor: '#ADADAD',
      gridColor: '#595959',
      evolution: {
        red: '#FF8F94',
        green: '#81EA9C'
      },
      accents: [{
        background: "#4B4B4B",
        left: "#FFEED4",
        right: "#C49EFF",
        color: "#FAFAFA"
      }, {
        background: "#4B4B4B",
        left: "#9FEFB4",
        right: "#C1E9FF",
        color: "#FAFAFA"
      }, {
        background: "#4B4B4B",
        left: "#CD93EB",
        right: "#FEFFC3",
        color: "#FAFAFA"
      }]
    }
  }
};
export class Evolution {
  constructor({
    type = null,
    options = CHART_PERIOD_COMPARE_OPTIONS
  } = {}) {
    this.options = options; // [{ text, value, label }]
    this.valueOptions = options.map(o => o.value); // ["prev","yoy"]
    this.type = type;
  }
  set(type) {
    if (this.valueOptions.includes(type) || type === null) {
      this.type = type;
    }
  }
  toggle(type) {
    if (!this.valueOptions.includes(type)) return;
    this.type = this.type === type ? null : type;
  }
  is(type) {
    return this.type === type;
  }
  isPrevious() {
    return this.is("prev");
  }
  isYoY() {
    return this.is("yoy");
  }
  getActiveOption() {
    return this.valueOptions.includes(this.type) ? this._getOptionData(this.type) : null;
  }
  _getOptionData(value) {
    return this.options.find(o => o.value === value) || null;
  }
  get label() {
    const active = this.getActiveOption();
    return active?.label ?? null;
  }
  get text() {
    const active = this.getActiveOption();
    return active?.text ?? null;
  }
  getState() {
    return {
      type: this.type,
      options: this.options
    };
  }
  static fromState(state) {
    return new Evolution({
      type: state.type,
      options: state.options
    });
  }
}

// ----------------------------
// ChartPeriod Class
// ----------------------------
export class ChartPeriod {
  constructor(defaultPeriod = PERIOD_OPTIONS[PERIOD_GRANULARITIES.WEEKLY][0], currentPeriodExcluded = true, periodOption = PERIOD_GRANULARITIES.WEEKLY) {
    this.period = defaultPeriod;
    this.currentPeriodExcluded = currentPeriodExcluded;
    this.periodOption = periodOption;
    this._periodsMatrix = {}; // Memoization for periods
  }
  get periods() {
    return this._getPeriodsMatrix(this.periodOption);
  }
  setPeriod(v) {
    this.period = v;
  }
  setPeriodOption(option) {
    if (PERIOD_OPTIONS[option]) {
      this.periodOption = option;
      this.period = PERIOD_OPTIONS[option][0]; // Set to the default (13 units) for the new option
    }
  }
  _getPeriodsMatrix(option) {
    if (!this._periodsMatrix[option]) {
      this._periodsMatrix[option] = PERIOD_OPTIONS[option];
    }
    return this._periodsMatrix[option];
  }
}

// ----------------------------
// ChartMetric Class
// ----------------------------
export class ChartMetric {
  constructor({
    key,
    label,
    color,
    type = "number",
    visible = true
  }) {
    this.key = key;
    this.label = label;
    this.type = type; // "currency", "number", "percentage"
    this.visible = visible;
    this.color = color;
    this.evolution = new Evolution();
  }
  toggleVisibility() {
    this.visible = !this.visible;
  }
  setVisibility(v) {
    this.visible = v;
  }
  getFormatter(currencyConfig = {}) {
    let formatter;
    switch (this.type) {
      case "currency":
        formatter = value => getFormattedCurrency(value, false, currencyConfig.currency, 0, undefined, undefined, 999);
        break;
      case "number":
        formatter = value => getFormattedDecimal(value, 0, false);
        break;
      case "percentage":
        formatter = value => getFormattedPercentage(value, 1);
        break;
      default:
        formatter = v => v;
    }
    return createFormatterWithNullCheck(formatter);
  }
  get showEvolution() {
    return this.evolution.isPrevious() || this.evolution.isYoY();
  }
  getState() {
    return {
      key: this.key,
      label: this.label,
      color: this.color,
      type: this.type,
      visible: this.visible,
      evolution: this.evolution.getState()
    };
  }
  static fromState(state) {
    const metric = new ChartMetric({
      key: state.key,
      label: state.label,
      color: state.color,
      type: state.type,
      visible: state.visible
    });
    metric.evolution = Evolution.fromState(state.evolution);
    return metric;
  }
}

// ----------------------------
// EvolutionChartEngine Class
// ----------------------------
export class EvolutionChartEngine {
  constructor(periodOption = PERIOD_GRANULARITIES.WEEKLY, dateFormats = DEFAULT_DATE_FORMATS) {
    this.chartPeriod = new ChartPeriod(PERIOD_OPTIONS[periodOption][0], true, periodOption);
    this._theme = null;
    this.rawData = null;
    this._metric1 = null;
    this._metric2 = null;
    this.dateFormat = null;
    this.currencyConfig = {};
    this.extraConfig = {};
    this.periodKey = "period_agg";
    this.metricFiltered = [];
    this.showLabels = true;
    this.showAxes = true;
    this.matchScale = false;
    this.chartType = "line-chart";
    this.showX2Label = false;
    this._lastChartConfig = null;
    this.dateFormats = dateFormats;
  }

  // ----------------------------
  // Metric getters/setters
  // ----------------------------
  get metric1() {
    return this._metric1;
  }
  set metric1(value) {
    this._metric1 = value instanceof ChartMetric ? value : new ChartMetric(value);
    this.theme = this._theme;
  }
  get metric2() {
    return this._metric2;
  }
  set metric2(value) {
    this._metric2 = value instanceof ChartMetric ? value : new ChartMetric(value);
    this.theme = this._theme;
  }

  // ----------------------------
  // Public methods
  // ----------------------------
  setData(data, metrics, dateFormat, currencyConfig, extraConfig = {}) {
    this.rawData = data;
    this.dateFormat = dateFormat;
    this.currencyConfig = currencyConfig;
    this.extraConfig = extraConfig;
    [this._metric1, this._metric2] = metrics.map(m => m ? m instanceof ChartMetric ? m : new ChartMetric(m) : null);
    this.theme = CHART_THEME.colors.light.accents[0];
  }
  updateData(data) {
    this.rawData = data;
  }
  updateMetrics(metrics) {
    [this._metric1, this._metric2] = metrics.map(m => m instanceof ChartMetric ? m : new ChartMetric(m));
  }
  updateCurrencyConfig(cfg) {
    this.currencyConfig = cfg;
  }
  setChartPeriod(v) {
    this.chartPeriod.setPeriod(v);
  }
  setChartPeriodOption(option) {
    this.chartPeriod.setPeriodOption(option);
  }
  get theme() {
    return this._theme;
  }
  set theme(accent) {
    if (!accent) return;
    const whichTheme = (() => {
      return accent.background === CHART_THEME.colors.light.accents[0].background ? 'light' : 'dark';
    })();
    this._theme = accent;
    if (this._metric1) {
      this._metric1.color = accent.left;
    }
    if (this._metric2) {
      this._metric2.color = accent.right;
    }
    this.textColor = CHART_THEME.colors[whichTheme].textColor;
    this.scaleColor = CHART_THEME.colors[whichTheme].scaleColor;
    this.gridColor = CHART_THEME.colors[whichTheme].gridColor;
    this.evolutionColors = CHART_THEME.colors[whichTheme].evolution;
  }
  get getChartThemes() {
    return Object.values(CHART_THEME.colors);
  }

  // ----------------------------
  // Private helpers
  // ----------------------------
  _filterByPeriod(data) {
    if (!data?.length) return data;
    const {
      value,
      unit
    } = this.chartPeriod.period;
    if (!value || !unit) return data;
    const startDate = moment().subtract(value, unit).startOf("day");
    return data.filter(entry => moment(entry[this.periodKey]).isSameOrAfter(startDate));
  }
  _excludeCurrent(data) {
    if (!this.chartPeriod.currentPeriodExcluded) return data;
    const last = data[data.length - 1];
    if (!last) return data;
    const lastDate = moment(last[this.periodKey]);
    const now = moment();
    const {
      unit
    } = this.chartPeriod.period;
    if (lastDate.isSame(now, unit)) return data.slice(0, -1);
    return data;
  }
  getEmptyChartConfig() {
    return {
      data: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        },
        scales: {}
      }
    };
  }
  _buildLabels(data) {
    const format = this.dateFormats[this.chartPeriod.periodOption?.toUpperCase()] || "D MMM";
    return data.map(entry => processDate(entry[this.periodKey], format));
  }
  _extractMetricData(data, metricKey) {
    return data.map(entry => {
      const val = entry[metricKey];
      return val?.parsedValue ?? val ?? null;
    });
  }
  // Compute global min/max for scale matching
  _getGlobalMinMax(filtered, visibleMetrics) {
    let allValues = [];
    for (const m of visibleMetrics) {
      const values = this._extractMetricData(filtered, m.key);
      allValues.push(...values);
    }
    if (!allValues.length) return {
      min: 0,
      max: 0
    };
    return {
      min: Math.min(...allValues),
      max: Math.max(...allValues)
    };
  }
  _buildDataset(metric, index, data) {
    let type;

    // Assign type based on chartType and index
    if (this.chartType === "bar-chart") {
      type = "bar"; // Both metrics are bars
    } else if (this.chartType === "line-chart") {
      type = "line"; // Both metrics are lines
    } else if (this.chartType === "bar-line-chart") {
      type = index === 0 ? "bar" : "line"; // First is bar, second is line
    } else {
      type = "line"; // Default to line
    }
    // Set the order: line (index 1) should appear above bar (index 0)
    const order = type === "line" ? 0 : 1;
    return {
      label: metric.label.replace(/_/g, " "),
      data: this._extractMetricData(data, metric.key),
      borderColor: metric.color,
      backgroundColor: metric.color,
      tension: 0.3,
      fill: false,
      yAxisID: index === 0 ? "y" : "y" + index,
      type,
      // Use the determined type
      order,
      pointStyle: 'circle',
      ...this.extraConfig
    };
  }
  _buildScale(metric, index) {
    const baseYAxisId = "y";
    const yAxisID = index === 0 ? baseYAxisId : `${baseYAxisId}${index}`;
    const tickCallback = metric.getFormatter(this.currencyConfig);
    return {
      [yAxisID]: {
        type: "linear",
        display: true,
        position: index % 2 === 0 ? "left" : "right",
        ticks: {
          display: this.showAxes,
          // <--- hide axis labels if showAxes = false
          callback: tickCallback,
          color: this.textColor,
          font: {
            family: "Figtree, sans-serif",
            size: 12,
            weight: 400
          },
          padding: 16
        },
        grid: {
          display: false
        },
        border: {
          display: this.showAxes,
          color: 'transparent'
        }
      }
    };
  }

  // ----------------------------
  // Main transformation
  // ----------------------------

  _getFilteredData() {
    return this._filterByPeriod(this._excludeCurrent(this.rawData));
  }
  transformData(data) {
    this.updateData(data);
    const allMetrics = [this._metric1, this._metric2].filter(Boolean);
    const visibleMetrics = allMetrics.filter(m => m.visible);
    if (!data || !visibleMetrics.length) return this.getEmptyChartConfig();
    const filtered = this._getFilteredData();
    this.metricFiltered = this._mapOverMetric(filtered, this.metric1);
    const labels = this._buildLabels(filtered);

    // ----------------------------
    // Build datasets
    // ----------------------------
    const datasets = visibleMetrics.map((metric, index) => this._buildDataset(metric, index, filtered));

    // ----------------------------
    // Build Y scales (left & right)
    // ----------------------------
    const scales = visibleMetrics.reduce((acc, metric, index) => {
      return {
        ...acc,
        ...this._buildScale(metric, index)
      };
    }, {});

    // ----------------------------
    // Match scale (if applicable)
    // ----------------------------
    if (this.matchScale && visibleMetrics.length > 1) {
      const {
        min,
        max
      } = this._getGlobalMinMax(filtered, visibleMetrics);
      Object.keys(scales).forEach(id => {
        if (id.startsWith("y")) {
          scales[id].min = min;
          scales[id].max = max;
        }
      });
    }
    scales.x = {
      display: true,
      position: "bottom",
      ticks: {
        display: this.showAxes,
        // hide x-axis labels if showAxes = false
        color: this.textColor,
        font: {
          family: "Figtree, sans-serif",
          size: 12,
          weight: 400
        }
      },
      grid: {
        display: false
      },
      border: {
        display: this.showAxes,
        // hide axis line if showAxes = false
        color: this.scaleColor
      }
    };

    // ----------------------------
    // Base options
    // ----------------------------
    let options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: this.metric1.showEvolution ? 20 : 0 // Make space for evolution labels
        }
      },
      scales,
      backgroundColor: this.theme.background,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          // Enable tooltips
          mode: 'index',
          // Show tooltips for all datasets at the same index
          intersect: false,
          // Show tooltips even if not directly hovering a point
          callbacks: {
            label: context => {
              const metric = visibleMetrics[context.datasetIndex];
              const value = context.raw;
              return metric.getFormatter(this.currencyConfig)(value);
            },
            title: () => null // Hide the default title if not needed
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleFont: {
            family: "Figtree, sans-serif",
            size: 12
          },
          bodyFont: {
            family: "Figtree, sans-serif",
            size: 12
          },
          padding: 10,
          displayColors: true
        },
        extraHeader: {
          text: this.metric1.evolution.label
        },
        datalabels: {
          display: this.showLabels ? "auto" : false,
          anchor: "end",
          align: "top",
          formatter: (value, ctx) => {
            const metric = visibleMetrics[ctx.datasetIndex];
            return metric.getFormatter(this.currencyConfig)(value);
          },
          font: {
            family: "Figtree, sans-serif",
            size: 12,
            weight: 400
          },
          color: "#ADADAD"
        }
      }
    };

    // ----------------------------
    // Final chart config
    // ----------------------------
    const finalChartConfig = {
      data: {
        labels,
        datasets
      },
      options: {
        ...options,
        evolutionValues: this.metricFiltered,
        metric: this.metric1 // or this.metric2, or both if needed
      }
    };
    this._lastChartConfig = {
      ...finalChartConfig
    };
    return finalChartConfig;
  }
  swap() {
    if (this._metric1 && this._metric2) {
      const temp = this._metric1;
      this._metric1 = this._metric2;
      this._metric2 = temp;
      this.transformData(this.rawData);
    }
  }
  _mapOverMetric(data, metric) {
    if (!metric) return [];
    if (data.length <= 0) return [];
    const evolutionMetrics = Object.keys(data[0]).filter(k => {
      // Previous period evolution
      if (metric.evolution.isPrevious() && k == `${metric.key}_evol`) return true;

      // Year-on-year evolution
      if (metric.evolution.isYoY() && k == `last_year_${metric.key}_evol`) return true;
      return false;
    });
    const evolutionFormatter = createEvolutionFormatter();
    return data.map(entry => {
      return evolutionMetrics.map(ev => ({
        value: evolutionFormatter(entry[ev]),
        color: this._getEvolutionColor(entry[ev])
      })).pop();
    }).filter(Boolean);
  }
  _getEvolutionColor(value, mode = EVOLUTION_MODES.POSITIVE) {
    if (value == 0) return '#000';
    if (mode === EVOLUTION_MODES.POSITIVE) {
      return value < 0 ? this.evolutionColors.red : this.evolutionColors.green;
    } else {
      return value > 0 ? this.evolutionColors.red : this.evolutionColors.green;
    }
  }
  get chartTypes() {
    return [{
      key: "line-chart"
    }, {
      key: "bar-chart"
    }, {
      key: "bar-line-chart"
    }];
  }

  /**
     * Extract the engine's state into a plain JS object.
     * This excludes Vue proxies and class methods.
     */

  getState() {
    return {
      chartPeriod: {
        periodOption: this.chartPeriod.periodOption,
        period: {
          ...this.chartPeriod.period
        },
        currentPeriodExcluded: this.chartPeriod.currentPeriodExcluded
      },
      metric1: this.metric1 ? this.metric1.getState() : null,
      metric2: this.metric2 ? this.metric2.getState() : null,
      rawData: this.rawData ? JSON.parse(JSON.stringify(this.rawData)) : [],
      // flags
      showLabels: this.showLabels,
      showAxes: this.showAxes,
      matchScale: this.matchScale,
      showX2Label: this.showX2Label,
      chartType: this.chartType,
      // config
      periodKey: this.periodKey,
      dateFormats: this.dateFormats,
      dateFormat: this.dateFormat,
      currencyConfig: {
        ...this.currencyConfig
      },
      extraConfig: JSON.parse(JSON.stringify(this.extraConfig)),
      // theme input only
      theme: this.theme ? {
        ...this.theme
      } : null
    };
  }

  /**
  * Recreate a new engine from a saved state object.
  * @param {Object} state State captured by getState()
  * @returns {EvolutionChartEngine}
  */
  static fromState(state) {
    const engine = new EvolutionChartEngine(state.chartPeriod.periodOption, state.dateFormats);

    // chart period
    engine.chartPeriod.setPeriod(state.chartPeriod.period);
    engine.chartPeriod.currentPeriodExcluded = state.chartPeriod.currentPeriodExcluded;

    // metrics (only if present)
    if (state.metric1) engine.metric1 = ChartMetric.fromState(state.metric1);
    if (state.metric2) engine.metric2 = ChartMetric.fromState(state.metric2);

    // config
    engine.rawData = state.rawData;
    engine.periodKey = state.periodKey;
    engine.currencyConfig = state.currencyConfig || {};
    engine.extraConfig = state.extraConfig || {};
    engine.dateFormat = state.dateFormat;

    // flags
    engine.showLabels = state.showLabels;
    engine.showAxes = state.showAxes;
    engine.matchScale = state.matchScale;
    engine.showX2Label = state.showX2Label;
    engine.chartType = state.chartType;

    // theme LAST (this sets derived colors safely)
    if (state.theme) {
      engine.theme = state.theme;
    }

    // 🔥 recompute everything
    engine.transformData(engine.rawData);
    return engine;
  }
  clone({
    dateFormats = null,
    forceShowAxes = null
  } = {}) {
    const state = this.getState();
    if (dateFormats) state.dateFormats = dateFormats;
    if (forceShowAxes !== null) state.showAxes = forceShowAxes;
    return EvolutionChartEngine.fromState(state);
  }
  limit(maxPoints = 7) {
    // Cap the export at min(currently selected period, maxPoints)
    const currentValue = this.chartPeriod.period?.value ?? maxPoints;
    const targetValue = Math.min(currentValue, maxPoints);
    const target = this.chartPeriod.periods.find(p => p.value === targetValue);
    if (target) {
      this.setChartPeriod(target);
      this.transformData(this.rawData);
    }
    return this._lastChartConfig;
  }
}